import React, { useEffect, useRef, useState } from 'react';
import { keyframes, styled } from '@withjoy/joykit';
import { addSuppressedError } from '@shared/utils/logger';
import { getBackdropImageUrl, isBackdropImage } from '@shared/utils/digitalCardBackdrop/digitalCardBackdrop';

/** Matches the wallpaper colour's own transition on `Layout.Main`, so a colour and an image change alike. */
const FADE_DURATION_MS = 300;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/**
 * A backdrop image, fixed to the viewport rather than covering its surface, because the surfaces grow:
 * opening the envelope stacks the card's front and back, and a `cover` image would scale up to that
 * height. A dedicated fixed element rather than `background-attachment: fixed`, which iOS Safari ignores
 * (see `PremiumCardTemplate`).
 *
 * The surface's `clip-path` keeps the layer inside the surface -- it clips fixed descendants, where
 * `overflow` does not -- and makes the surface a stacking context, so `z-index: -1` sits behind the
 * surface's content and nothing else. Nested surfaces each paint the same viewport-sized crop, so they
 * line up without a seam.
 */
const BackdropLayer = styled.div<{ $url: string; $isLeaving: boolean }>`
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background-image: url(${props => props.$url});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  animation: ${fadeIn} ${FADE_DURATION_MS}ms ease-in-out;
  opacity: ${props => (props.$isLeaving ? 0 : 1)};
  transition: opacity ${FADE_DURATION_MS}ms ease-in-out;

  /* Shortened rather than removed, so the animation and transition events that clear old layers still fire. */
  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
    transition-duration: 1ms;
  }
`;

type Layer = Readonly<{ id: number; url: string; isLeaving: boolean }>;

/** Resolves once the image is downloaded and decoded, so it fades in whole rather than painting in. */
const loadImage = (url: string): Promise<void> => {
  const img = new Image();
  img.src = url;
  if (typeof img.decode === 'function') return img.decode();
  return new Promise((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load backdrop: ${url}`));
  });
};

/**
 * Paints a stored digital card backdrop -- a colour, or a backdrop image's id -- on a surface whose
 * content can grow, fading between backdrops the way `Layout.Main` fades between colours. Spread
 * `surfaceProps` on the surface and render `layers` as its first child.
 *
 * - A new image is fetched and decoded first, and the old backdrop stays up until it fades in on top.
 * - An image giving way to a colour: the colour goes underneath at once and the image fades off it.
 * - A colour giving way to an image: the last colour stays underneath until the image arrives. With no
 *   colour before it -- a guest opening the card -- that is a neutral fill.
 * - Only the latest choice fades in; a load overtaken by another choice is dropped. A failed load keeps
 *   whatever was showing.
 *
 * The clip stays on while any layer is up, including one fading off, so it never spills past the surface.
 */
export const useDigitalCardBackdrop = (value: string | undefined) => {
  const targetUrl = getBackdropImageUrl(value);
  const [layers, setLayers] = useState<ReadonlyArray<Layer>>([]);
  const nextIdRef = useRef(0);

  const lastColorRef = useRef<string | undefined>(undefined);
  if (value && !isBackdropImage(value)) lastColorRef.current = value;

  useEffect(() => {
    if (!targetUrl) {
      setLayers(current => current.map(layer => ({ ...layer, isLeaving: true })));
      return;
    }
    let isCurrent = true;
    loadImage(targetUrl)
      .then(() => {
        if (!isCurrent) return;
        setLayers(current => {
          const top = current[current.length - 1];
          if (top && top.url === targetUrl && !top.isLeaving) return current;
          return [...current, { id: nextIdRef.current++, url: targetUrl, isLeaving: false }];
        });
      })
      .catch(error => {
        if (isCurrent) addSuppressedError(error, { context: 'useDigitalCardBackdrop', url: targetUrl });
      });
    return () => {
      isCurrent = false;
    };
  }, [targetUrl]);

  /** The incoming layer now covers everything under it, so those can go. */
  const handleFadeInEnd = (id: number) => setLayers(current => current.slice(current.findIndex(layer => layer.id === id)));
  const handleFadeOutEnd = (id: number) => setLayers(current => current.filter(layer => layer.id !== id));

  const isImageTarget = !!targetUrl;
  const surfaceProps = {
    backgroundColor: isImageTarget ? (lastColorRef.current ?? 'mono2') : value,
    clipPath: layers.length > 0 || isImageTarget ? 'inset(0)' : undefined,
  };

  const layerElements = layers.map(layer => (
    <BackdropLayer
      key={layer.id}
      aria-hidden
      data-testid="digital-card-backdrop-layer"
      $url={layer.url}
      $isLeaving={layer.isLeaving}
      onAnimationEnd={() => handleFadeInEnd(layer.id)}
      onTransitionEnd={() => layer.isLeaving && handleFadeOutEnd(layer.id)}
    />
  ));

  return { surfaceProps, layers: <>{layerElements}</> };
};
